import React from 'react';
import { Calendar, ChevronRight, Clock, MapPin, Zap, CheckCircle2, Package, Activity, AlertCircle } from 'lucide-react';
import { RepairJob, JobStatus, UserRole, Language } from '../types';
import { useTranslation } from '../LanguageContext';

interface RepairJobCardProps {
  job: RepairJob;
  role: UserRole;
  onStatusChange?: (id: string, newStatus: JobStatus) => void;
}

export const RepairJobCard: React.FC<RepairJobCardProps> = ({ job, role }) => {
  const { language } = useTranslation();
  const isShop = role === UserRole.SHOP_OWNER || role === UserRole.STAFF;

  const getStatusConfig = (status: JobStatus) => {
    const labels: Record<string, Record<Language, string>> = {
      [JobStatus.PENDING]: { EN: 'Awaiting', KH: 'រង់ចាំការបញ្ជាក់', ZH: '待确认', JA: '確認待ち', KO: '확인 대기' },
      [JobStatus.CONFIRMED]: { EN: 'Confirmed', KH: 'បានបញ្ជាក់', ZH: '已确认', JA: '確認済み', KO: '확인됨' },
      [JobStatus.DIAGNOSING]: { EN: 'Diagnosing', KH: 'កំពុងពិនិត្យ', ZH: '诊断中', JA: '診断中', KO: '진단 중' },
      [JobStatus.IN_PROGRESS]: { EN: 'Repairing', KH: 'កំពុងជួសជុល', ZH: '维修中', JA: '修理中', KO: '수리 중' },
      [JobStatus.COMPLETED]: { EN: 'Completed', KH: 'រួចរាល់', ZH: '已完成', JA: '完了', KO: '완료' },
      [JobStatus.REJECTED]: { EN: 'Rejected', KH: 'បានបដិសេធ', ZH: '已拒绝', JA: '拒否', KO: '거절됨' },
    };

    switch (status) {
      case JobStatus.PENDING: 
        return { label: labels[status][language], color: 'text-amber-500', bg: 'bg-amber-50', icon: Clock, step: 1 };
      case JobStatus.CONFIRMED: 
        return { label: labels[status][language], color: 'text-blue-500', bg: 'bg-blue-50', icon: CheckCircle2, step: 2 };
      case JobStatus.DIAGNOSING: 
        return { label: labels[status][language], color: 'text-indigo-500', bg: 'bg-indigo-50', icon: Activity, step: 3 };
      case JobStatus.IN_PROGRESS: 
        return { label: labels[status][language], color: 'text-violet-500', bg: 'bg-violet-50', icon: Zap, step: 4 };
      case JobStatus.COMPLETED: 
        return { label: labels[status][language], color: 'text-emerald-500', bg: 'bg-emerald-50', icon: Package, step: 5 };
      case JobStatus.REJECTED: 
        return { label: labels[status][language], color: 'text-red-500', bg: 'bg-red-50', icon: AlertCircle, step: 0 };
      default: 
        return { label: status, color: 'text-slate-400', bg: 'bg-slate-50', icon: Clock, step: 0 };
    }
  };

  const config = getStatusConfig(job.status);
  
  const stepLabels: Record<Language, string[]> = {
    EN: ['Booked', 'Confirmed', 'Diagnosis', 'Fixing', 'Done'],
    KH: ['បានកក់', 'បញ្ជាក់', 'រោគវិនិច្ឆ័យ', 'ជួសជុល', 'រួចរាល់'],
    ZH: ['已预订', '已确认', '已诊断', '维修中', '完成'],
    JA: ['予約済み', '確認済み', '診断中', '修理中', '完了'],
    KO: ['예약됨', '확인됨', '진단 중', '수리 중', '완료']
  };

  const steps = stepLabels[language];
  const currentStep = config.step;

  const uiTexts: Record<string, Record<Language, string>> = {
    estimate: { EN: 'Estimate', KH: 'តម្លៃប៉ាន់ស្មាន', ZH: '估价', JA: '見積もり', KO: '견적' },
    awaiting: { EN: 'Checking...', KH: 'រង់ចាំពិនិត្យ', ZH: '检查中...', JA: '確認中...', KO: '확인 중...' }
  };

  return (
    <div className="group bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-violet-200/40 transition-all duration-500 relative overflow-hidden">
      {job.status === JobStatus.IN_PROGRESS && (
        <div className="absolute top-0 right-0 p-4">
          <div className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-violet-500"></span>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-shrink-0 flex md:flex-col items-center md:items-start gap-4 border-b md:border-b-0 md:border-r border-slate-50 pb-4 md:pb-0 md:pr-8">
          <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center text-2xl font-black text-white shadow-xl shadow-slate-200">
            {(isShop ? job.customerName : job.shopName).charAt(0)}
          </div>
          <div>
            <h4 className="font-black text-slate-900 text-xl tracking-tight leading-none mb-1">
              {isShop ? job.customerName : job.shopName}
            </h4>
            <div className="flex items-center text-slate-400 text-[10px] font-black uppercase tracking-widest">
              <Calendar size={12} className="mr-1.5" />
              {new Date(job.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-4">
            <div className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${config.bg} ${config.color} border border-current opacity-80`}>
              <config.icon size={12} className="mr-2" />
              {config.label}
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">{uiTexts.estimate[language]}</p>
              <span className="text-sm font-black text-slate-900">{job.estimatedCost || uiTexts.awaiting[language]}</span>
            </div>
          </div>

          <p className="text-slate-500 text-sm font-medium mb-8 line-clamp-2 leading-relaxed">
            {job.description}
          </p>
          
          {job.status !== JobStatus.REJECTED && (
            <div className="relative">
              <div className="flex justify-between mb-2">
                {steps.map((s, i) => (
                  <div key={s} className="flex flex-col items-center gap-1">
                    <div className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${i + 1 <= currentStep ? 'bg-violet-600 scale-125 shadow-lg shadow-violet-200' : 'bg-slate-100'}`} />
                    <span className={`text-[8px] font-black uppercase tracking-tighter ${i + 1 <= currentStep ? 'text-slate-900' : 'text-slate-300'}`}>
                      {s}
                    </span>
                  </div>
                ))}
              </div>
              <div className="absolute top-1.5 left-0 w-full h-0.5 bg-slate-50 -z-10">
                <div 
                  className="h-full bg-violet-600 transition-all duration-1000 ease-out shadow-sm"
                  style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between md:flex-col md:justify-center md:items-end gap-4">
          <button className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-400 flex items-center justify-center group-hover:bg-violet-600 group-hover:text-white group-hover:shadow-xl transition-all duration-300">
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};
